<script setup lang="ts">
    import { onMounted, inject, reactive, computed } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { SubscriptionAccess } from '@views/BackOffice/Subscription/Subscription.access';
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { FormProps } from '@shared/components/Form/Form';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const subscriptionAccess: SubscriptionAccess = injectorAccess.injector.get(SubscriptionAccess);

    const state = reactive<{
        companyForm: FormProps;
        companyFormReady: boolean;
    }>({
        companyForm: {
            disableSubmit: false,
            alert: null,
            data: {},
        },
        companyFormReady: false,
    });

    onMounted(async () => {
        setupCompanyFormData();

        await subscriptionAccess.getIndustries().then(() => {
            setIndustryOnCompanyFormData();
        });

        await subscriptionAccess.getCountries().then(() => {
            setCountryOnCompanyFormData();
        });
    });

    function setupCompanyFormData(): void {
        state.companyForm.data = {
            PaymentPeriod: {
                modelValue: paymentPeriodLabel.value,
                group: 'Billing Cycle & Method',
                required: false,
                textOrHtmlText: {
                    label: $Resources.BillingPeriodLabel,
                },
            },
            PaymentMethod: {
                modelValue: paymentMethodLabel.value,
                group: 'Billing Cycle & Method',
                required: false,
                textOrHtmlText: {
                    label: $Resources.BillingMethodLabel,
                },
            },
            Name: {
                modelValue: subscriptionAccess.state.boClientCompany!.Name,
                group: 'Company info',
                required: true,
                input: {
                    label: 'Name',
                },
            },
            IndustryId: {
                modelValue: subscriptionAccess.state.boClientCompany!.IndustryId,
                group: 'Company info',
                required: false,
                select: {
                    label: 'Industry',
                    options: [],
                    readonly: true,
                },
            },
            BillingName: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingName,
                group: 'Billing info',
                required: false,
                input: {
                    label: `Företagsnamn (om annat än ${subscriptionAccess.state.boClientCompany!.Name})`,
                },
            },
            BillingRecipientName: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingRecipientName,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Namn på faktura',
                },
            },
            BillingEmail: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingEmail,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Billing Email',
                    type: 'email',
                },
            },
            EInvoiceAddress: {
                modelValue: subscriptionAccess.state.boClientCompany!.EInvoiceAddress,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'E-Invoice Address',
                },
            },
            InvoicingInstructions: {
                modelValue: subscriptionAccess.state.boClientCompany!.InvoicingInstructions,
                group: 'Billing info',
                required: false,
                textArea: {
                    label: 'Invoicing instructions',
                },
            },
            BillingAddress: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingAddress,
                group: 'Billing info',
                required: false,
                textArea: {
                    label: 'Billing Address (replaced by detailed fields)',
                },
            },
            BillingAttention: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingAttention,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Attention',
                },
            },
            BillingAddress1: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingAddress1,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Address1',
                },
            },
            BillingAddress2: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingAddress2,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'Address2',
                },
            },
            BillingZipCode: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingZipCode,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'ZipCode',
                },
            },
            BillingCity: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingCity,
                group: 'Billing info',
                required: false,
                input: {
                    label: 'City',
                },
            },
            BillingCountry: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingCountry,
                group: 'Billing info',
                required: true,
                select: {
                    label: 'Country',
                    options: [],
                    readonly: true,
                },
            },
            Url: {
                modelValue: subscriptionAccess.state.boClientCompany!.Url,
                group: 'Company info',
                required: false,
                input: {
                    label: 'URL',
                    type: 'url',
                },
            },
            BusinessId: {
                modelValue: subscriptionAccess.state.boClientCompany!.BusinessId,
                group: 'Company info',
                required: false,
                input: {
                    label: 'Business ID',
                    placeholder: 'Ange organisationsnummer eller motsvarande',
                },
            },
            VatNumber: {
                modelValue: subscriptionAccess.state.boClientCompany!.VatNumber,
                group: 'Company info',
                required: false,
                input: {
                    label: 'VAT Number',
                    placeholder: 'Ange momsregistreringsnummer om företaget är i ett EU-land',
                },
            },
            IsEuCompany: {
                modelValue: subscriptionAccess.state.boClientCompany!.IsEuCompany,
                group: 'Company info',
                required: false,
                checkbox: {
                    label: 'Is EU Company',
                },
            },
            Contact: {
                modelValue: subscriptionAccess.state.boClientCompany!.Contact,
                group: 'Contract contact',
                required: false,
                input: {
                    label: 'Contract contact',
                },
            },
            Email: {
                modelValue: subscriptionAccess.state.boClientCompany!.Email,
                group: 'Contract contact',
                required: false,
                input: {
                    label: 'Contract email',
                    type: 'email',
                },
            },
            Phone: {
                modelValue: subscriptionAccess.state.boClientCompany!.Phone,
                group: 'Contract contact',
                required: false,
                input: {
                    label: 'Contract phone',
                    type: 'tel',
                },
            },
            BillingContactName: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingContactName,
                group: 'Billing contact',
                required: false,
                input: {
                    label: 'Billing Contact Name',
                },
            },
            BillingContactEmail: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingContactEmail,
                group: 'Billing contact',
                required: false,
                input: {
                    label: 'Billing Contact Email',
                    type: 'email',
                },
            },
            BillingContactPhone: {
                modelValue: subscriptionAccess.state.boClientCompany!.BillingContactPhone,
                group: 'Billing contact',
                required: false,
                input: {
                    label: 'Billing Contact Phone',
                    type: 'tel',
                },
            },
            SupportContactName: {
                modelValue: subscriptionAccess.state.boClientCompany!.SupportContactName,
                group: 'Support contact',
                required: false,
                input: {
                    label: 'Support Contact Name',
                },
            },
            SupportContactEmail: {
                modelValue: subscriptionAccess.state.boClientCompany!.SupportContactEmail,
                group: 'Support contact',
                required: false,
                input: {
                    label: 'Support Contact Email',
                    type: 'email',
                },
            },
            SupportContactPhone: {
                modelValue: subscriptionAccess.state.boClientCompany!.SupportContactPhone,
                group: 'Support contact',
                required: false,
                input: {
                    label: 'Support Contact Phone',
                    type: 'tel',
                },
            },
            PasswordExpireDays: {
                modelValue: subscriptionAccess.state.boClientCompany!.PasswordExpireDays,
                group: 'Security',
                required: false,
                input: {
                    label: 'Password Expire Days',
                    type: 'number',
                },
            },
        };

        state.companyFormReady = true;
    }

    function setIndustryOnCompanyFormData(): void {
        let industriesAsOptions = subscriptionAccess.state.industries.map((industry) => {
            return { value: industry.IndustryId, label: industry.Name };
        });

        state.companyForm.data.IndustryId.select!.options = industriesAsOptions;
    }

    function setCountryOnCompanyFormData(): void {
        let countriesAsOptions = subscriptionAccess.state.countries.map((country) => {
            return { value: country, label: country };
        });

        state.companyForm.data.BillingCountry.select!.options = countriesAsOptions;
    }

    const paymentPeriodLabel = computed(() => {
        if (!subscriptionAccess.state.boServiceSubscription) return '';

        return `${subscriptionAccess.state.boServiceSubscription.PaymentPeriod} ${$Resources.RecurrenceLabelMonths}`;
    });

    const paymentMethodLabel = computed(() => {
        switch (subscriptionAccess.state.boServiceSubscription?.DefaultPaymentMethod) {
            case 1:
                return 'Credit card';
            case 2:
                return 'Invoice';
            case 3:
                return 'Paper invoice';
            case 4:
                return 'E-invoice';
            default:
                return '';
        }
    });

    async function saveCompany() {
        if (!state.companyFormReady) return;

        state.companyForm.setInitData = false;

        const company = subscriptionAccess.state.boClientCompany!;
        const companyData = state.companyForm.data;

        company.Name = companyData.Name.modelValue;
        company.IndustryId = companyData.IndustryId.modelValue;
        company.BillingName = companyData.BillingName.modelValue;
        company.BillingRecipientName = companyData.BillingRecipientName.modelValue;
        company.BillingAddress = companyData.BillingAddress.modelValue;
        company.BillingAttention = companyData.BillingAttention.modelValue;
        company.BillingAddress1 = companyData.BillingAddress1.modelValue;
        company.BillingAddress2 = companyData.BillingAddress2.modelValue;
        company.BillingZipCode = companyData.BillingZipCode.modelValue;
        company.BillingCity = companyData.BillingCity.modelValue;
        company.BillingCountry = companyData.BillingCountry.modelValue;
        company.BillingEmail = companyData.BillingEmail.modelValue;
        company.EInvoiceAddress = companyData.EInvoiceAddress.modelValue;
        company.InvoicingInstructions = companyData.InvoicingInstructions.modelValue;
        company.Url = companyData.Url.modelValue;
        company.BusinessId = companyData.BusinessId.modelValue;
        company.VatNumber = companyData.VatNumber.modelValue;
        company.IsEuCompany = companyData.IsEuCompany.modelValue;
        company.Contact = companyData.Contact.modelValue;
        company.Email = companyData.Email.modelValue;
        company.Phone = companyData.Phone.modelValue;
        company.BillingContactName = companyData.BillingContactName.modelValue;
        company.BillingContactEmail = companyData.BillingContactEmail.modelValue;
        company.BillingContactPhone = companyData.BillingContactPhone.modelValue;
        company.SupportContactName = companyData.SupportContactName.modelValue;
        company.SupportContactEmail = companyData.SupportContactEmail.modelValue;
        company.SupportContactPhone = companyData.SupportContactPhone.modelValue;
        company.PasswordExpireDays = companyData.PasswordExpireDays.modelValue;

        const success = await subscriptionAccess.saveClientCompany(company);
        if (success) state.companyForm.setInitData = true;
    }
</script>

<template>
    <Form
        v-if="state.companyFormReady"
        class="fieldset-grid"
        :data="state.companyForm.data"
        :setInitData="state.companyForm.setInitData"
        @update:isValid="(key, newVal) => (state.companyForm!.data[key].isValid = newVal)"
        @update:modelValue="(key, newVal) => (state.companyForm.data[key].modelValue = newVal)"
        @onSubmit="saveCompany"></Form>
</template>

<style scoped>
    .fieldset-grid {
        max-width: 800px;
    }
</style>
